import * as T from "../utilities/frontendTypes";
import { queryKeys } from "../utilities/queryKeys";
import { useMutationWithUI } from "../utilities/useMutationWithUI";
import {
  createFounderApplication,
  createFounderProgramApplication,
  founderProgramEnroll,
  founderApproveSow,
  editedApplicationChange,
  sendSpfConfirm,
  sendSpfRequest,
  reapplyFromCompletedProject,
  pdfUpload,
  sendHelpEmail,
} from "./founderService";
import { FormData } from "../Components/Form/Form";
import textData from "../textData.json";

export function useCreateFounderApplication() {
  return useMutationWithUI({
    mutationFn: (programId: T.ProgramId) => createFounderApplication(programId),
    // latestApplication: the rejected dashboard renders reapplyEligibility
    // from it. dashboards.all (Rule 2): a submission is admin-visible —
    // flaggedNote advisory, auto-reject records, admin dashboard counts.
    invalidateKeys: [queryKeys.dashboards.all, queryKeys.members.latestApplication()],
    // No static successMessage: an autoReject response is a real outcome (an
    // application record WAS created, then system-rejected), so it must reach
    // onSuccess for the page to update status/navigate instead of leaving the
    // submit form armed for duplicate submissions. The page shows the
    // outcome-appropriate toast itself.
    checkSuccess: (data: T.FounderMembershipApplicationResponse) => data?.success === true || data?.autoReject === true,
  });
}

export function useCreateFounderProgramApp() {
  return useMutationWithUI({
    mutationFn: (args: { programId: T.ProgramId; additionalData: FormData | undefined }) =>
      createFounderProgramApplication(args.programId, args.additionalData),
    invalidateKeys: [queryKeys.dashboards.founder()],
    successMessage: textData.Toasts.Founder.ApplicationSubmitted,
  });
}

export function useFounderProgramEnroll() {
  return useMutationWithUI({
    mutationFn: (programId: T.ProgramId) => founderProgramEnroll(programId),
    invalidateKeys: [queryKeys.dashboards.founder()],
    successMessage: textData.Toasts.Founder.Enrolled,
  });
}

export function useFounderApproveSow() {
  return useMutationWithUI({
    mutationFn: (sowId: string) => founderApproveSow(sowId),
    invalidateKeys: [queryKeys.dashboards.founder(), queryKeys.sows.all],
    successMessage: textData.Toasts.Founder.SowApproved,
  });
}

export function useEditedApplicationChange() {
  return useMutationWithUI({
    mutationFn: () => editedApplicationChange(),
    invalidateKeys: [queryKeys.dashboards.founder()],
    successMessage: textData.Toasts.Founder.ApplicationUpdated,
  });
}

export function useSendSpfConfirm() {
  return useMutationWithUI({
    mutationFn: (spf: string) => sendSpfConfirm(spf),
    invalidateKeys: [queryKeys.dashboards.founder(), queryKeys.members.latestApplication()],
  });
}

export function useSendSpfRequest() {
  return useMutationWithUI({
    mutationFn: (newSpfDetails: string) => sendSpfRequest(newSpfDetails),
    invalidateKeys: [queryKeys.dashboards.founder(), queryKeys.members.latestApplication()],
  });
}

export function useReapply() {
  return useMutationWithUI({
    mutationFn: () => reapplyFromCompletedProject(),
    invalidateKeys: [queryKeys.dashboards.founder()],
    successMessage: textData.Toasts.Founder.ReapplicationSubmitted,
  });
}

export function useFounderPdfUpload() {
  return useMutationWithUI<T.HTTPRequestSuccessFlag, globalThis.FormData>({
    mutationFn: (form: globalThis.FormData) => pdfUpload(form),
    invalidateKeys: [queryKeys.dashboards.founder()],
    showLoadingOverlay: false,
    checkSuccess: () => true,
  });
}

export function useSendHelpEmail() {
  return useMutationWithUI({
    mutationFn: (emailDetails: { message: string; helpCategory: string }) =>
      sendHelpEmail(emailDetails),
    invalidateKeys: [], // Help email — no UI state changes
    successMessage: textData.Toasts.Founder.EmailSent,
  });
}
